import { useState, useEffect } from 'react'
import { DashboardLayout } from '@/components/app/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AuthAPI, QuestionnaireAPI } from '@/services/api'
import { QuestionType, QuestionnaireCategory, Question } from '@/types/dashboard.types'
import { CATEGORY_CONFIG } from '@/constants/dashboard.constants'
import { Plus, Edit, Trash2, Eye, ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

const AGE_OPTIONS = [2, 3, 4, 5]
const AGE_OPTIONS_UI = [2, 3, 4, 5]
const SYSTEM_DEFAULT_QUESTIONNAIRE_TITLE = 'TinyTalk Comprehensive Speech & Language Screening'
const DEFAULT_QUESTIONNAIRE_READ_ONLY_MAX_ORDER = 40

type AdminQuestionnaireItem = {
  id: string
  title: string
  description: string
  minAge: number
  maxAge: number
  version: string
  isActive: boolean
  _count?: {
    questions: number
  }
}

export const QuestionnaireManagement = () => {
  const [user, setUser] = useState<any>(null)
  const [questionnaires, setQuestionnaires] = useState<AdminQuestionnaireItem[]>([])
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<string | null>(null)
  const [questionnaire, setQuestionnaire] = useState<any>(null)
  const [questions, setQuestions] = useState<Question[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isActivatingId, setIsActivatingId] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null)
  const [expandedCategories, setExpandedCategories] = useState<QuestionnaireCategory[]>([
    QuestionnaireCategory.SPEECH_SOUND,
  ])
  const [showEnglish, setShowEnglish] = useState(false)
  const [selectedAgeFilter, setSelectedAgeFilter] = useState<number | null>(null)

  const adminInputClass = 'selection:bg-blue-500 selection:text-white'
  const adminFieldClass = `${adminInputClass} bg-muted border-transparent placeholder:text-muted-foreground focus-visible:ring-teal-500`

  const [formData, setFormData] = useState({
    questionText: '',
    questionTextEnglish: '',
    questionType: QuestionType.MULTIPLE_CHOICE,
    category: QuestionnaireCategory.SPEECH_SOUND,
    options: [''],
    ageYearsVisible: [...AGE_OPTIONS_UI] as number[],
  })

  const isSystemDefaultQuestionnaire = questionnaire?.title === SYSTEM_DEFAULT_QUESTIONNAIRE_TITLE

  const canModifyQuestion = (question: Pick<Question, 'order'>): boolean => {
    if (!isSystemDefaultQuestionnaire) {
      return true
    }

    return question.order > DEFAULT_QUESTIONNAIRE_READ_ONLY_MAX_ORDER
  }

  const getDefaultAgeVisibility = () =>
    selectedAgeFilter === null ? [...AGE_OPTIONS_UI] : [selectedAgeFilter]

  const getCanonicalQuestionnaireId = (list: AdminQuestionnaireItem[]): string | null => {
    const systemDefault = list.find((item) => item.title === SYSTEM_DEFAULT_QUESTIONNAIRE_TITLE)
    if (systemDefault) {
      return systemDefault.id
    }

    if (list.length === 0) {
      return null
    }

    const sortedByQuestionCount = [...list].sort(
      (a, b) => (b._count?.questions ?? 0) - (a._count?.questions ?? 0)
    )

    return sortedByQuestionCount[0]?.id || null
  }

  const refreshQuestionnaires = async (preferredId?: string | null) => {
    const list = await QuestionnaireAPI.getAdminQuestionnaires()
    setQuestionnaires(list)

    const canonicalId = getCanonicalQuestionnaireId(list)
    const hasPreferred = preferredId && list.some((item) => item.id === preferredId)

    const nextSelected =
      (hasPreferred ? preferredId : null) ||
      canonicalId ||
      list.find((item) => item.isActive)?.id ||
      list[0]?.id ||
      null

    setSelectedQuestionnaireId(nextSelected)
    return list
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [u] = await Promise.all([
          AuthAPI.me(),
        ])
        setUser(u.user)

        const list = await refreshQuestionnaires()
        if (list.length === 0) {
          setQuestionnaire(null)
          setQuestions([])
        }

        setLoadError(null)
      } catch (err) {
        console.error('Failed to load questionnaire data', err)
        setLoadError('Failed to load questionnaire data.')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    const fetchSelectedQuestionnaire = async () => {
      if (!selectedQuestionnaireId) {
        setQuestionnaire(null)
        setQuestions([])
        return
      }

      try {
        const qData = await QuestionnaireAPI.getQuestionnaire(selectedQuestionnaireId)
        setQuestionnaire(qData)
        setQuestions(qData.questions || [])
      } catch (err) {
        console.error('Failed to load selected questionnaire details', err)
        setLoadError('Failed to load selected questionnaire details.')
      }
    }

    fetchSelectedQuestionnaire()
  }, [selectedQuestionnaireId])


  const resetForm = () => {
    setFormData({
      questionText: '',
      questionTextEnglish: '',
      questionType: QuestionType.MULTIPLE_CHOICE,
      category: QuestionnaireCategory.SPEECH_SOUND,
      options: [''],
      ageYearsVisible: getDefaultAgeVisibility(),
    })
  }

  const handleActivateQuestionnaire = async (questionnaireId: string) => {
    const target = questionnaires.find((item) => item.id === questionnaireId)
    if ((target?._count?.questions || 0) === 0) {
      toast.error('Cannot activate questionnaire with 0 questions. Add questions first.')
      return
    }

    try {
      setIsActivatingId(questionnaireId)
      await QuestionnaireAPI.activateQuestionnaire(questionnaireId)
      toast.success('Questionnaire activated successfully')
      await refreshQuestionnaires(questionnaireId)
    } catch (error: any) {
      console.error('Failed to activate questionnaire', error)
      toast.error(error?.response?.data?.message || 'Failed to activate questionnaire')
    } finally {
      setIsActivatingId(null)
    }
  }

  const handleAddQuestion = async () => {
    if (!questionnaire?.id) return

    try {
      const nextOrder = questions.length > 0 ? Math.max(...questions.map((q) => q.order || 0)) + 1 : 1
      const ageYearsVisible = selectedAgeFilter === null
        ? formData.ageYearsVisible
        : [selectedAgeFilter]

      const payload = {
        questionText: formData.questionText,
        questionTextEnglish: formData.questionTextEnglish,
        questionType: formData.questionType,
        category: formData.category,
        order: nextOrder,
        options: formData.questionType === QuestionType.MULTIPLE_CHOICE ? formData.options : null,
        ageYearsVisible,
        isRequired: true,
      }
      const newQ = await QuestionnaireAPI.createQuestion(questionnaire.id, payload)
      setQuestions([...questions, newQ])
      setIsAddDialogOpen(false)
      resetForm()
      await refreshQuestionnaires(questionnaire.id)
      toast.success('Question added successfully')
    } catch (err) {
      console.error('Failed to add question', err)
      toast.error('Hindi ma-dagdag ang tanong. Subukang muli.')
    }
  }

  const handleEditQuestion = async () => {
    if (!selectedQuestion) return
    if (!canModifyQuestion(selectedQuestion)) {
      toast.error('Only newly added questions (41+) can be edited in the default questionnaire.')
      return
    }

    try {
      const payload = {
        questionText: formData.questionText,
        questionTextEnglish: formData.questionTextEnglish,
        questionType: formData.questionType,
        category: formData.category,
        options: formData.questionType === QuestionType.MULTIPLE_CHOICE ? formData.options : null,
        ageYearsVisible: formData.ageYearsVisible,
      }
      const updated = await QuestionnaireAPI.updateQuestion(selectedQuestion.id, payload)
      setQuestions(questions.map((q) => q.id === selectedQuestion.id ? { ...q, ...updated } : q))
      setIsEditDialogOpen(false)
      setSelectedQuestion(null)
      resetForm()
      toast.success('Question updated successfully')
    } catch (err) {
      console.error('Failed to update question', err)
      toast.error('Hindi ma-update ang tanong. Subukang muli.')
    }
  }

  const handleDeleteQuestion = async (questionId: string) => {
    const targetQuestion = questions.find((question) => question.id === questionId)
    if (targetQuestion && !canModifyQuestion(targetQuestion)) {
      toast.error('Only newly added questions (41+) can be deleted in the default questionnaire.')
      return
    }

    if (!confirm('Sigurado ka bang gusto mong i-delete ang tanong na ito?')) return
    try {
      await QuestionnaireAPI.deleteQuestion(questionId)
      setQuestions(questions.filter((q) => q.id !== questionId))
      await refreshQuestionnaires(questionnaire?.id || selectedQuestionnaireId)
      toast.success('Question deleted successfully')
    } catch (err) {
      console.error('Failed to delete question', err)
      toast.error('Hindi ma-delete ang tanong. Subukang muli.')
    }
  }

  const handleOpenEdit = (question: Question) => {
    if (!canModifyQuestion(question)) {
      toast.error('Only newly added questions (41+) can be edited in the default questionnaire.')
      return
    }

    setSelectedQuestion(question)
    setFormData({
      questionText: question.questionText,
      questionTextEnglish: question.questionTextEnglish || '',
      questionType: question.questionType,
      category: question.category,
      options: question.options || [''],
      ageYearsVisible: question.ageYearsVisible && question.ageYearsVisible.length > 0
        ? question.ageYearsVisible
        : [...AGE_OPTIONS_UI],
    })
    setIsEditDialogOpen(true)
  }

  const toggleAgeVisibility = (ageYears: number) => {
    const current = formData.ageYearsVisible
    const exists = current.includes(ageYears)

    if (exists) {
      const next = current.filter((value) => value !== ageYears)
      if (next.length === 0) {
        return
      }
      setFormData((prev) => ({ ...prev, ageYearsVisible: next }))
      return
    }

    setFormData((prev) => ({
      ...prev,
      ageYearsVisible: [...prev.ageYearsVisible, ageYears].sort((a, b) => a - b),
    }))
  }

  const handleAddOption = () => {
    setFormData((prev) => ({ ...prev, options: [...prev.options, ''] }))
  }

  const handleRemoveOption = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }))
  }

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...formData.options]
    newOptions[index] = value
    setFormData((prev) => ({ ...prev, options: newOptions }))
  }

  const toggleCategory = (category: QuestionnaireCategory) => {
    if (expandedCategories.includes(category)) {
      setExpandedCategories(expandedCategories.filter((c) => c !== category))
    } else {
      setExpandedCategories([...expandedCategories, category])
    }
  }

  const questionsByCategory = Object.values(QuestionnaireCategory).map((category) => ({
    category,
    questions: questions.filter((q) => q.category === category),
  }))

  const normalizeAgeVisibility = (ageYearsVisible?: number[]) =>
    (ageYearsVisible || [])
      .filter((age) => AGE_OPTIONS.includes(age))
      .sort((a, b) => a - b)

  const getQuestionsForSelectedAge = (sourceQuestions: Question[]) => {
    if (selectedAgeFilter === null) {
      return sourceQuestions
    }

    return sourceQuestions.filter((question) =>
      normalizeAgeVisibility(question.ageYearsVisible).includes(selectedAgeFilter)
    )
  }

  const displayQuestions =
    getQuestionsForSelectedAge(questions)

  const displayQuestionsByCategory = Object.values(QuestionnaireCategory).map((category) => ({
    category,
    questions: displayQuestions.filter((q) => q.category === category),
  }))

  const renderQuestionDialog = (isEdit = false) => (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
      <DialogHeader>
        <DialogTitle>{isEdit ? 'Edit Question' : 'Add New Question'}</DialogTitle>
        <DialogDescription>
          {isEdit
            ? 'Update the question details below.'
            : 'Create a new question for the assessment questionnaire.'}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4 max-h-[65vh] overflow-y-auto pr-1">
        {/* Filipino Question Text */}
        <div className="space-y-2">
          <Label htmlFor="questionText">
            Question Text (Filipino) <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="questionText"
            value={formData.questionText}
            onChange={(e) => setFormData((prev) => ({ ...prev, questionText: e.target.value }))}
            className={adminFieldClass}
            placeholder="Enter question in Filipino"
            rows={3}
          />
        </div>

        {/* English Translation */}
        <div className="space-y-2">
          <Label htmlFor="questionTextEnglish">Question Text (English Translation)</Label>
          <Textarea
            id="questionTextEnglish"
            value={formData.questionTextEnglish}
            onChange={(e) => setFormData((prev) => ({ ...prev, questionTextEnglish: e.target.value }))}
            className={adminFieldClass}
            placeholder="Enter English translation"
            rows={3}
          />
        </div>

        {/* Question Type */}
        <div className="space-y-2">
          <Label htmlFor="questionType">Question Type</Label>
          <Select
            value={formData.questionType}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, questionType: value as QuestionType }))
            }
          >
            <SelectTrigger id="questionType" className="bg-muted border-transparent focus:ring-teal-500">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[90] bg-popover text-popover-foreground border border-border shadow-lg opacity-100">
              <SelectItem value={QuestionType.MULTIPLE_CHOICE}>Multiple Choice</SelectItem>
              <SelectItem value={QuestionType.YES_NO}>Yes/No</SelectItem>
              <SelectItem value={QuestionType.FREE_TEXT}>Free Text</SelectItem>
              <SelectItem value={QuestionType.RATING_SCALE}>Rating Scale (1-5)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Category */}
        <div className="space-y-2">
          <Label htmlFor="category">Category (Domain)</Label>
          <Select
            value={formData.category}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, category: value as QuestionnaireCategory }))
            }
          >
            <SelectTrigger id="category" className="bg-muted border-transparent focus:ring-teal-500">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[90] bg-popover text-popover-foreground border border-border shadow-lg opacity-100">
              {Object.values(QuestionnaireCategory).map((cat) => {
                const CatIcon = CATEGORY_CONFIG[cat].Icon
                return (
                  <SelectItem key={cat} value={cat}>
                    <span className="flex items-center gap-2">
                      <CatIcon className="h-4 w-4 shrink-0" aria-hidden />
                      {CATEGORY_CONFIG[cat].label}
                    </span>
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Options (for Multiple Choice) */}
        {formData.questionType === QuestionType.MULTIPLE_CHOICE && (
          <div className="space-y-2 pt-1">
            <Label>Answer Options</Label>
            <div className="space-y-2">
              {formData.options.map((option, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    className={adminFieldClass}
                    placeholder={`Option ${index + 1}`}
                  />
                  {formData.options.length > 1 && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleRemoveOption(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={handleAddOption} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Add Option
            </Button>
          </div>
        )}

        <div className="space-y-2">
          <Label>Age Visibility (Question Filter)</Label>
          {selectedAgeFilter !== null ? (
            <div className="rounded-md border border-primary/20 bg-primary/10 px-3 py-2 text-sm text-primary">
              This question will be saved for age {selectedAgeFilter} years old (based on selected filter).
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                Select which ages can see this question during assessment.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {AGE_OPTIONS_UI.map((ageYears) => {
                  const checked = formData.ageYearsVisible.includes(ageYears)
                  return (
                    <label
                      key={ageYears}
                      className="flex items-center gap-2 rounded-md border border-border p-2 text-sm cursor-pointer hover:bg-muted/60"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleAgeVisibility(ageYears)}
                      />
                      <span>{ageYears} years old</span>
                    </label>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => {
            isEdit ? setIsEditDialogOpen(false) : setIsAddDialogOpen(false)
            resetForm()
          }}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          onClick={isEdit ? handleEditQuestion : handleAddQuestion}
          disabled={!formData.questionText}
        >
          {isEdit ? 'Update Question' : 'Add Question'}
        </Button>
      </DialogFooter>
    </DialogContent>
  )

  if (isLoading) {
    return (
      <DashboardLayout user={user || { firstName: 'Admin', lastName: '', email: '', role: 'ADMIN' }}>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout user={user}>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Questionnaire Management</h1>
            <p className="text-muted-foreground mt-2">
              Manage assessment questionnaires by age range and category
            </p>
          </div>

          {questionnaire && (
            <div className="flex items-center gap-2">
              <Button
                variant={showEnglish ? "default" : "outline"}
                onClick={() => setShowEnglish(!showEnglish)}
                className="gap-2"
              >
                <Eye className="h-4 w-4" />
                {showEnglish ? 'Hide English' : 'Show English'}
              </Button>

              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" variant="default" className="gap-2" onClick={resetForm}>
                    <Plus className="h-5 w-5" />
                    Add Question
                  </Button>
                </DialogTrigger>
                {renderQuestionDialog()}
              </Dialog>
            </div>
          )}
        </div>

        {!questionnaire ? (
          <Card className="border border-amber-500/30 bg-amber-500/10 dark:border-amber-500/25 dark:bg-amber-950/30">
            <CardHeader>
              <CardTitle className="text-amber-900 dark:text-amber-200">No Questionnaire Selected</CardTitle>
              <CardDescription>
                {loadError || 'Create a questionnaire or use the age filter to manage an existing one.'}
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <>
            {/* Questionnaire Info */}
            <Card className="border-transparent bg-gradient-to-br from-teal-50 to-teal-100/50 backdrop-blur-sm dark:from-card dark:to-muted/50 dark:border dark:border-border">
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle className="text-2xl">All ages</CardTitle>
                      <CardDescription className="mt-2">
                        Filter the questionnaire questions by age.
                      </CardDescription>
                    </div>
                    <Badge variant="outline" className={questionnaire.isActive ? 'bg-emerald-500/10 text-emerald-800 border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-500/40' : 'bg-muted text-muted-foreground border-border'}>
                      {questionnaire.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="flex flex-col gap-2 sm:max-w-[240px]">
                    <span className="text-xs text-muted-foreground">Filter by age</span>
                    <Select
                      value={selectedAgeFilter === null ? 'all' : String(selectedAgeFilter)}
                      onValueChange={(value) => setSelectedAgeFilter(value === 'all' ? null : Number(value))}
                    >
                      <SelectTrigger className="w-full bg-background border-border">
                        <SelectValue placeholder="Select age" />
                      </SelectTrigger>
                      <SelectContent className="z-50 bg-popover text-popover-foreground border border-border shadow-lg opacity-100">
                        <SelectItem value="all">All ages</SelectItem>
                        {AGE_OPTIONS_UI.map((age) => (
                          <SelectItem key={age} value={String(age)}>
                            {age} years old
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                  <div>
                    <span className="text-muted-foreground">Total Questions:</span>
                    <span className="ml-2 font-semibold">{displayQuestions.length}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Age Range:</span>
                    <span className="ml-2 font-semibold">
                      {(questionnaire.minAge / 12).toFixed(1)} - {Math.min(questionnaire.maxAge / 12, 5).toFixed(1)} years
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Questions by Category */}
            <div className="space-y-4">
              {displayQuestionsByCategory.map(({ category, questions: catQuestions }) => {
                const CategoryHeaderIcon = CATEGORY_CONFIG[category].Icon
                return (
                  <Card key={category} className="border-transparent bg-muted/30 backdrop-blur-sm">
                    <CardHeader
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => toggleCategory(category)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CategoryHeaderIcon
                            className="h-6 w-6 shrink-0"
                            style={{ color: CATEGORY_CONFIG[category].color }}
                            aria-hidden
                          />
                          <div>
                            <CardTitle className="text-lg">{CATEGORY_CONFIG[category].label}</CardTitle>
                            <CardDescription>
                              {catQuestions.length} question{catQuestions.length !== 1 ? 's' : ''}
                            </CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{catQuestions.length}</Badge>
                          {expandedCategories.includes(category) ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </CardHeader>

                    {expandedCategories.includes(category) && (
                      <CardContent className="space-y-3">
                        {catQuestions.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No questions in this category yet.
                          </p>
                        ) : (
                          catQuestions.map((question) => (
                            <Card key={question.id} className="border-transparent bg-card hover:border-border hover:shadow-md transition-all duration-300 dark:border dark:border-border">
                              <CardContent className="pt-6">
                                <div className="flex items-start gap-4">
                                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold text-sm">
                                    {question.order}
                                  </div>
                                  <div className="flex-1 space-y-2">
                                    <div>
                                      <p className="font-medium">{question.questionText}</p>
                                      {showEnglish && question.questionTextEnglish && (
                                        <p className="text-sm text-blue-600 dark:text-blue-400 mt-1 italic">
                                          English: {question.questionTextEnglish}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className="text-xs">
                                        {question.questionType.replace('_', ' ')}
                                      </Badge>
                                      {question.options && (
                                        <span className="text-xs text-muted-foreground">
                                          {question.options.length} options
                                        </span>
                                      )}
                                      {normalizeAgeVisibility(question.ageYearsVisible).length > 0 && (
                                        <span className="text-xs text-muted-foreground">
                                          Ages: {normalizeAgeVisibility(question.ageYearsVisible).join(', ')}
                                        </span>
                                      )}
                                      {normalizeAgeVisibility(question.ageYearsVisible).length === 0 && (
                                        <span className="text-xs font-medium text-red-600">
                                          No age tags
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {canModifyQuestion(question) ? (
                                    <div className="flex gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleOpenEdit(question)}
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-500/10 dark:hover:bg-red-950/40"
                                        onClick={() => handleDeleteQuestion(question.id)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-md whitespace-nowrap">
                                      ML core
                                    </span>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))
                        )}
                      </CardContent>
                    )}
                  </Card>
                )
              })}
            </div>
          </>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          {renderQuestionDialog(true)}
        </Dialog>
      </div>
    </DashboardLayout>
  )
}
